import React, { useEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { DNABox } from '@alucio/lux-ui';
import { useAppSettings } from 'src/state/context/AppSettings';
import useIsPublisherView from 'src/hooks/useIsPublisherView/useIsPublisherView';

import SearchInput from 'src/components/Header/SearchInput/SearchInput'

import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage'
import useScreenNav from 'src/components/DNA/hooks/useScreenNav'

import ProfileMenu from 'src/components/DNA/Header/ProfileMenu'
import Notification from 'src/components/Header/Notifications';
import NetworkOfflineButton from 'src/components/DNA/Header/NetworkOfflineButton'
import SyncStatusButton from 'src/components/DNA/Header/SyncStatusButton'
import MeetingButton from 'src/components/Header/MeetingButton/MeetingButton';
import { useCRMStatus } from 'src/hooks/useCRMStatus'
import { CRMIntegrationProvider } from 'src/screens/Profile/CRMIntegration'
import { useSyncState } from 'src/state/redux/selector/cache'
import CustomDeckButton from 'src/components/Header/CustomDeckButton/CustomDeckButton'
import StandaloneFormsButton from './StandaloneFormsButton';
import { FORMS, useTenantStandaloneForms } from 'src/state/redux/selector/crm'
import { StandaloneFormUsage } from '@alucio/aws-beacon-amplify/src/models'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import NetworkConnectionBanner from 'src/components/NetworkConnection/NetworkConnectionBanner'

const styles = StyleSheet.create({
  headerContainer: {
    paddingLeft: 32,
    paddingRight: 16,
    paddingVertical: 12,
  },
  headerContainerSmallBreakpoint: {
    padding: 16,
    gap: 16,
  },
  headerActionButtonsContainer: {
    gap: 16,
    marginLeft: 16,
  },
})

const useDNAAppHeader = () => {
  const { deviceMode } = useAppSettings()
  const { goTo } = useScreenNav()
  const currentPage = useCurrentPage()
  /** Route config options */
  const {
    disableGlobalHeader,
    disableMeetings,
    disableSearch,
    disableNotificaions,
  } = { ...currentPage?.configOptions?.[deviceMode] }
  const showMeetingsButton = !disableMeetings

  return {
    disableGlobalHeader,
    disableSearch,
    disableNotificaions,
    deviceMode,
    showMeetingsButton,
    goToHome: () => goTo.HOME(),
  }
}

const HeaderActionButtons: React.FC = () => {
  const currentPage = useCurrentPage();
  const isNavigationMenuDisabled = currentPage?.configOptions?.desktop?.disableGlobalNavigationMenu;
  const { showMeetingsButton, disableNotificaions } = useDNAAppHeader();
  const { isOnline, isOfflineEnabled } = useAppSettings();
  const { displayCRMConnectHeaderButton } = useCRMStatus();
  const customForms = useTenantStandaloneForms([FORMS.BEACON, FORMS.CRM], StandaloneFormUsage.STANDALONE);
  const featureFlags = useFeatureFlags('BEAC_6655_custom_deck_usage', 'enableOfflineMode');
  const isPublisherView = useIsPublisherView();

  const showCreateCustomDeckButton =
    !isPublisherView &&
    featureFlags.BEAC_6655_custom_deck_usage &&
    !isNavigationMenuDisabled;
  const showStandaloneFormsButton = !!customForms.length && !isPublisherView;
  const showSyncStatusButton =
    ((isOfflineEnabled && featureFlags.enableOfflineMode) || displayCRMConnectHeaderButton) &&
    !isNavigationMenuDisabled;

  return (
    <DNABox alignX="end" alignY="center" style={styles.headerActionButtonsContainer}>
      <DNABox alignY="center" spacing="sm">
        { showMeetingsButton && <MeetingButton /> }
        { showCreateCustomDeckButton && <CustomDeckButton mode="header" appearance="outline" status="tertiary"/> }
      </DNABox>
      {/* [TODO-POLISH] - Double check to make sure this renders the same (without the wrapping DNABox)  */}
      <DNABox alignY="center" spacing="sm">
        { !isOnline && <NetworkOfflineButton /> }
        { showSyncStatusButton && <SyncStatusButton />}
        { showStandaloneFormsButton && <StandaloneFormsButton />}
        { !disableNotificaions && <Notification />}
      </DNABox>
      <ProfileMenu />
    </DNABox>
  )
}

const DNAAppHeaderUI2: React.FC = () => {
  const { disableSearch } = useDNAAppHeader();
  const { isCompactLayoutDeprecate } = useAppSettings();
  const currentPage = useCurrentPage();

  // Check if navigation menu is disabled on current route
  const isNavigationMenuDisabled = currentPage?.configOptions?.desktop?.disableGlobalNavigationMenu;

  const headerContainerStyle = useMemo(
    () => isCompactLayoutDeprecate
      ? styles.headerContainerSmallBreakpoint
      : styles.headerContainer,
    [isCompactLayoutDeprecate],
  )

  return (
    <DNABox appearance="col">
      <NetworkConnectionBanner />
      <DNABox
        appearance={isCompactLayoutDeprecate ? 'col' : 'row'}
        style={headerContainerStyle}
      >
        {
          disableSearch
            ? <DNABox fill={isNavigationMenuDisabled} />
            : <SearchInput />
        }
        <HeaderActionButtons />
      </DNABox>
    </DNABox>
  )
}

const DNAAppHeader = (props) => {
  const { disableGlobalHeader } = useDNAAppHeader()
  const syncState = useSyncState()

  // [TODO-REFACTOR] - Relocate this closer to syncing code, not randomly in some UI component
  useEffect(
    () => {
      if (syncState.context.lastSyncDate) {
        const lastSyncDate = new Date(syncState.context.lastSyncDate).toISOString()
        localStorage.setItem('lastSyncDate', lastSyncDate)
      }
    },
    [syncState.context.lastSyncDate],
  )

  /** No need to continue instantiating if this component is disabled on current route */
  if (disableGlobalHeader) return null

  return (
    <CRMIntegrationProvider>
      <DNAAppHeaderUI2 {...props} />
    </CRMIntegrationProvider>
  )
}

export default DNAAppHeader
